/* Factumis App — Tableau de bord (/dashboard).
   Vue synthétique : KPI financiers (encaissé / en attente / en retard), bannière
   de conformité DGFiP, alerte quota mensuel, notifications épinglées (3 premières),
   raccourcis d'action rapide, et liste des factures récentes.
   Données : useDashboard() → GET /api/b2b/dashboard ; useMe() → GET /api/me. */
import { useEffect, useState } from 'react';
import type { IconName } from '../lib/icons';
import { Icon } from '../lib/icons';
import { Button, ConfBanner, ConfirmDialog, KPI, StatusPill, rowActivation, eur, eurAmount } from '../components/ui';
import { MonthlyCaChart } from '../components/MonthlyCaChart';
import { useGo } from '../shell/nav';
import { useDashboard, useNotifications } from '../data/live';
import type { NotifType } from '../data/live';
import { useSession } from '../lib/auth-client';
// useMe lit le MeProvider partagé (monté dans Layout) — un seul GET /api/me pour tous.
import { useMe } from '../lib/useMe';
import { emittedQuotaLevel } from '../lib/quota';
import { apiGet, apiPost } from '../lib/api';
import { useKybLink, formatActivationDate } from '../lib/useKybLink';

// type de notification → icône (les notifs live ne portent pas d'icône, contrairement
// au jeu de démo retiré).
const NOTIF_ICON: Record<NotifType, IconName> = {
  overdue: 'clock',
  rejected: 'x-c',
  paid: 'check-c',
  received: 'file-in',
};

function ActionCard({ icon, title, sub, onClick }: { icon: Parameters<typeof Icon>[0]['name']; title: string; sub: string; onClick: () => void }) {
  return (
    <button
      className="card card--pad card--hover"
      onClick={onClick}
      style={{ display: 'flex', alignItems: 'center', gap: 14, textAlign: 'left', cursor: 'pointer', border: 'none', width: '100%' }}
    >
      <span className="ic-tile">
        <Icon name={icon} size={21} />
      </span>
      <span>
        <b style={{ display: 'block', fontSize: 14.5, fontWeight: 600 }}>{title}</b>
        <span className="muted" style={{ fontSize: 12.5 }}>
          {sub}
        </span>
      </span>
    </button>
  );
}

export function Dashboard() {
  const go = useGo();
  const session = useSession();
  const firstName = session.data?.user?.name?.split(' ')[0] ?? '';
  const { me } = useMe();
  const eq = emittedQuotaLevel(me);
  const dash = useDashboard();
  const recent = dash.recent;
  const { rows: notifs } = useNotifications();
  const pinned = notifs.slice(0, 3);
  // Conformité réelle : « en règle » seulement si le SIREN est renseigné ET la
  // plateforme connectée. Sinon on invite à compléter (au lieu de l'affirmer en dur).
  const orgSiren = me?.org?.siren ?? null;
  const compliant = !!orgSiren && me?.org?.paStatus === 'active';

  // Accusé affiché en modale (à la place des window.alert natifs) : message
  // simple, un seul bouton, rien à décider pour l'utilisateur.
  const [notice, setNotice] = useState<{ title: string; body: string } | null>(null);

  // Lien de vérification d'identité (KYB Dotfile) : hook partagé avec la page Conformité.
  const { openKybLink, kybLoading, kybNotice, setKybNotice } = useKybLink();
  const shownNotice = notice ?? kybNotice;

  // État et handler pour relancer le raccordement en cas d'échec
  const [retrying, setRetrying] = useState(false);
  const [retried, setRetried] = useState(false);
  async function retryProvisioning() {
    setRetrying(true);
    try {
      await apiPost('/api/mandate/reprovision', {});
      setRetried(true); // le passage effectif en 'active' arrivera par webhook, on affiche l'accusé
    } catch {
      setRetried(false);
      setNotice({ title: 'Relance impossible', body: 'Le raccordement n’a pas pu être relancé. Réessayez dans un instant.' });
    } finally {
      setRetrying(false);
    }
  }

  // Carte « Cotisations estimées » : fetch léger de la période la plus récente.
  // Masquée silencieusement (pas de carte) sur 409 MICRO_DISABLED (régime non activé)
  // ou toute autre erreur : cette carte est une commodité, jamais bloquante.
  const [micro, setMicro] = useState<{ period: string; amountCents: number; dueDate: string } | null>(null);
  useEffect(() => {
    const ctrl = new AbortController();
    apiGet<{ periods: { period: string; amountCents: number; dueDate: string }[] }>('/api/b2b/micro/contributions?limit=1', ctrl.signal)
      .then((d) => {
        if (!ctrl.signal.aborted && d.periods[0]) setMicro(d.periods[0]);
      })
      .catch(() => {});
    return () => ctrl.abort();
  }, []);

  return (
    <div>
      <ConfirmDialog open={shownNotice !== null} title={shownNotice?.title ?? ''} onClose={() => { setNotice(null); setKybNotice(null); }}>
        {shownNotice?.body ?? ''}
      </ConfirmDialog>

      {me?.org?.paStatus === 'provisioning' && me?.org?.paMandateStatus === 'pending_kyb' && (
        <div className="callout-info" style={{ marginBottom: 16 }}>
          <Icon name="info" size={18} />
          <div>
            <div className="t">Vérification d'identité requise pour activer votre raccordement.</div>
            <div className="d">
              Une vérification du représentant légal (KYB) est demandée par la plateforme de facturation.{' '}
              <Button variant="ghost" onClick={() => void openKybLink()} disabled={kybLoading} loading={kybLoading}>
                {kybLoading ? 'Ouverture…' : 'Compléter la vérification'}
              </Button>
            </div>
          </div>
        </div>
      )}
      {me?.org?.paStatus === 'provisioning' && me?.org?.paMandateStatus === 'created' && (
        <div className="callout-info" style={{ marginBottom: 16 }}>
          <Icon name="info" size={18} />
          <div>
            <div className="t">Identité vérifiée : votre raccordement est en cours d'activation.</div>
            <div className="d">
              {formatActivationDate(me.org.paMandateStartDate)
                ? `Activation automatique prévue le ${formatActivationDate(me.org.paMandateStartDate)}.`
                : 'Activation automatique sous 24 à 48 h.'}{' '}
              Vous recevrez un email dès que l'émission sera ouverte ; vous pouvez déjà préparer vos brouillons.
            </div>
          </div>
        </div>
      )}
      {me?.org?.paStatus === 'provisioning' && me?.org?.paMandateStatus !== 'pending_kyb' && me?.org?.paMandateStatus !== 'created' && (
        <div className="callout-info" style={{ marginBottom: 16 }}>
          <Icon name="info" size={18} />
          <p>Votre raccordement à la plateforme de facturation est en cours de validation. Vous pouvez préparer vos brouillons ; l'émission sera activée automatiquement.</p>
        </div>
      )}
      {me?.org?.paStatus === 'suspended' && (
        <div className="callout-err" style={{ marginBottom: 16 }}>
          <Icon name="alert" size={18} />
          <div>
            <div className="t">Mandat expiré ou révoqué : l'émission sera suspendue.</div>
            <div className="d"><a href="/mandat">Re-signer le mandat</a> pour réactiver votre raccordement.</div>
          </div>
        </div>
      )}
      {me?.org?.paStatus === 'failed' && !retried && (
        <div className="callout-err" style={{ marginBottom: 16 }}>
          <Icon name="alert" size={18} />
          <div>
            <div className="t">Le raccordement à la plateforme de facturation a échoué.</div>
            <div className="d">
              Vos brouillons restent disponibles ; l'émission sera activée dès le raccordement rétabli.{' '}
              <Button variant="ghost" onClick={() => void retryProvisioning()} disabled={retrying} loading={retrying}>
                {retrying ? 'Relance…' : 'Relancer le raccordement'}
              </Button>
            </div>
          </div>
        </div>
      )}
      {retried && (
        <div className="callout-info" style={{ marginBottom: 16 }}>
          <Icon name="info" size={18} />
          <p>Relance demandée : le raccordement est en cours de validation. Vous serez prévenu à l'activation.</p>
        </div>
      )}
      <div className="shead">
        <div className="shead__l">
          <div className="eyebrow">{firstName ? `Bonjour ${firstName}` : 'Bonjour'}</div>
          <h1>Votre activité en un coup d’œil</h1>
          <p>
            {compliant
              ? 'Tout est en règle pour le 1ᵉʳ septembre. Voici ce qui mérite votre attention aujourd’hui.'
              : 'Voici ce qui mérite votre attention aujourd’hui.'}
          </p>
        </div>
        <div className="shead__r">
          <Button variant="primary" icon="plus" onClick={() => go('create')}>
            Nouvelle facture
          </Button>
        </div>
      </div>

      {compliant ? (
        <ConfBanner
          title="Vous êtes en règle"
          subtitle="Connectée à une plateforme agréée DGFiP · archivage légal actif"
          meta={[
            { k: 'Plateforme', v: 'Connectée' },
            { k: 'Archivage', v: 'Actif' },
          ]}
          action={
            <Button
              variant="soft"
              size="sm"
              iconRight="arrow-r"
              onClick={() => go('conformity')}
              style={{ background: 'color-mix(in srgb, var(--blue-soft) 18%, transparent)', color: 'var(--blue-soft)' }}
            >
              Détails
            </Button>
          }
        />
      ) : (
        <ConfBanner
          variant="warn"
          title="Configuration à compléter"
          subtitle={orgSiren ? 'Vérifiez votre connexion à la plateforme agréée.' : 'Renseignez le SIREN de votre entreprise pour être prêt pour la réforme.'}
          action={
            <Button variant="soft" size="sm" iconRight="arrow-r" onClick={() => go(orgSiren ? 'conformity' : 'settings')}>
              {orgSiren ? 'Vérifier' : 'Compléter'}
            </Button>
          }
        />
      )}

      {eq.level !== 'ok' && (
        <ConfBanner
          variant="warn"
          title={eq.level === 'over' ? 'Forfait dépassé, émission maintenue' : 'Quota mensuel bientôt atteint'}
          subtitle={eq.level === 'over'
            ? `${eq.used} / ${eq.quota} factures émises ce mois-ci. Vos factures continuent de partir ; passez à une formule supérieure pour un quota plus large.`
            : `${eq.used} / ${eq.quota} factures émises ce mois-ci.`}
          action={
            <Button variant="soft" size="sm" iconRight="arrow-r" onClick={() => go('subscription')}>
              Voir les formules
            </Button>
          }
        />
      )}

      {micro && (
        <div className="card card--pad section-gap">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontWeight: 600 }}>Cotisations estimées ({micro.period})</div>
              <div className="muted" style={{ fontSize: 13.5 }}>
                {eur(micro.amountCents / 100)} à prévoir, déclaration avant le {new Date(`${micro.dueDate}T00:00:00`).toLocaleDateString('fr-FR')}.
              </div>
            </div>
            <Button variant="soft" size="sm" iconRight="arrow-r" onClick={() => go('micro')}>
              Voir le détail
            </Button>
          </div>
        </div>
      )}

      <div className="kpi-grid kpi-grid--3 section-gap">
        <KPI k="Encaissé ce mois" value={eurAmount(dash.encaisse)} cur="€" />
        <KPI k="En attente" value={eurAmount(dash.enAttente)} cur="€" />
        <KPI k="En retard" value={eurAmount(dash.enRetard)} cur="€" alert action="Voir" onAction={() => go('invoices')} />
      </div>

      <div style={{ display: 'grid', gap: 16, marginTop: 16 }} className="dash-grid">
        <div className="card">
          <div className="card__hd">
            <h3>Chiffre d’affaires · 6 derniers mois</h3>
          </div>
          <div className="card--pad">
            <MonthlyCaChart monthly={dash.monthly} />
          </div>
        </div>

        <div className="card">
          <div className="card__hd">
            <h3>Notifications épinglées</h3>
            <span className="lnk" onClick={() => go('notifications')}>
              Tout voir
            </span>
          </div>
          <div style={{ padding: '6px 0' }}>
            {pinned.length === 0 ? (
              <div className="muted" style={{ padding: '13px 20px', fontSize: 12.5 }}>Aucune notification pour le moment.</div>
            ) : pinned.map((n, i) => (
              <div key={n.key} style={{ display: 'flex', gap: 12, padding: '13px 20px', borderTop: i ? '1px solid var(--border-subtle)' : 'none' }}>
                <span className="ic-tile" style={{ width: 34, height: 34 }}>
                  <Icon name={NOTIF_ICON[n.type] ?? 'bell'} size={17} />
                </span>
                <span>
                  <b style={{ display: 'block', fontSize: 13.5, fontWeight: 600 }}>{n.title}</b>
                  <span className="muted" style={{ fontSize: 12.5 }}>
                    {n.body}
                  </span>
                  <span className="muted mono" style={{ fontSize: 11, display: 'block', marginTop: 3 }}>
                    {n.date}
                  </span>
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="qactions section-gap">
        <ActionCard icon="plus" title="Créer une facture" sub="En 3 minutes, conforme" onClick={() => go('create')} />
        <ActionCard icon="users" title="Ajouter un client" sub="Recherche par SIREN" onClick={() => go('clients')} />
        <ActionCard icon="file-in" title="Factures reçues" sub="Consulter et valider" onClick={() => go('received')} />
      </div>

      <div className="card section-gap">
        <div className="card__hd">
          <h3>Factures récentes</h3>
          <span className="lnk" onClick={() => go('invoices')}>
            Voir toutes
          </span>
        </div>
        {dash.source === 'error' ? (
          <div className="card--pad" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: '32px 20px' }}>
            <span className="muted">Impossible de charger les données.</span>
            <Button variant="ghost" size="sm" onClick={dash.reload}>Réessayer</Button>
          </div>
        ) : dash.source === 'live' && recent.length === 0 ? (
          <div className="card--pad" style={{ textAlign: 'center', padding: '32px 20px' }}>
            <span className="muted">Aucune facture pour le moment.</span>
          </div>
        ) : (
          <>
            <div className="hide-mob">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Client</th>
                    <th>Numéro</th>
                    <th>Échéance</th>
                    <th className="right">Montant</th>
                    <th>Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {recent.map((iv) => (
                    <tr key={iv.id} {...rowActivation(() => go('invoice', iv))}>
                      <td>
                        <div className="who-cell">
                          <b>{iv.client}</b>
                        </div>
                      </td>
                      <td className="num">{iv.id}</td>
                      <td className="muted">{iv.due}</td>
                      <td className="right amt">{eur(iv.amount)}</td>
                      <td>
                        <StatusPill status={iv.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="hide-desk" style={{ padding: 14 }}>
              <div className="mlist">
                {recent.map((iv) => (
                  <div key={iv.id} className="mrow" {...rowActivation(() => go('invoice', iv))}>
                    <div className="mrow__top">
                      <b>{iv.client}</b>
                      <StatusPill status={iv.status} />
                    </div>
                    <div className="mrow__meta">
                      <span className="num">{iv.id + ' · ' + iv.due}</span>
                      <span className="amt">{eur(iv.amount)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
